import {
  INITIAL_COLUMNS_STATE,
  TIME_RANGE_OPTIONS,
  TYPEOFCAMPAIGNS,
} from "./Toolbar/utils";
import calculateMarginAndRoi from "./Table/utils/calculateMarginAndRoi";
import { isRowSelectable } from "./Table/utils/rowSelectable";

// Filtros (y columnas visibles) que se persisten POR PAÍS en localStorage:
// única fuente de defaults para el estado inicial, el snapshot y la carga.
// Ver useDealsToolCountryFilters (sincronización) y el partialize de useStore.js
// (solo se persiste el mapa dtFiltersByCountry, no estas claves sueltas).
export const DT_PERSISTED_FILTER_DEFAULTS = {
  dealsToolSelectedBrand: null,
  dtSearch: [],
  dtSelectedCategoryPath: [],
  dtSelectedDomains: [],
  dtColumns: INITIAL_COLUMNS_STATE,
  dtRangeDate: null,
  dtSelectedTimeRange: "today",
  dtSelectedTypeofcampaign: [],
  dtSelectedStatus: [],
  dtSelectedMeliStatus: [],
  dtSelectedKam: [],
  dtSelectedPromotionName: [],
  // Filtro "Flags Meli" (slide en Más filtros): muestra solo Listings con otro vendedor
  // vinculado al catálogo y perdiendo el Buy Box (mismo criterio que BuyBoxAlert).
  dtShowMeliFlags: false,
};

export const dealsToolStoreSlice = (set, get) => ({
  ...DT_PERSISTED_FILTER_DEFAULTS,
  dtPaginationModel: {
    page: 0,
    pageSize: 10,
  },
  dtAllRows: [],
  dtRawRows: [],
  // Filas aplanadas con los filtros aplicados (todas las páginas, NO solo la visible).
  // dtAllRows trae solo la página actual y dtRawRows trae todo sin filtrar; el export
  // "todo lo que se ve" necesita exactamente este conjunto intermedio.
  dtFilteredRows: [],
  dtDomains: [],
  dtRowsSelectionModel: [],
  dtEditedRows: {},
  dtValidationErrors: {},
  // Revisión post-importación: mientras active=true la tabla muestra SOLO las filas del
  // último lote importado (ids). prevEditedRows/prevSelection son el snapshot previo al
  // import para poder restaurar al Cancelar. Ver importDtChanges / cancelDtImportReview.
  dtImportReview: {
    active: false,
    ids: [],
    prevEditedRows: null,
    prevSelection: null,
  },
  dtTimeRangeOptions: TIME_RANGE_OPTIONS,
  typeofcampaigns: TYPEOFCAMPAIGNS,
  dtFeedbackByRow: {},
  dtExpandedRows: {},
  // Snapshots de filtros por país ({ [countryId]: {...DT_PERSISTED_FILTER_DEFAULTS} }).
  // Es lo ÚNICO del Deals Tool que va a localStorage.
  dtFiltersByCountry: {},
  saveDtFiltersForCountry: (countryId) =>
    set((state) => ({
      dtFiltersByCountry: {
        ...state.dtFiltersByCountry,
        [countryId]: Object.fromEntries(
          Object.keys(DT_PERSISTED_FILTER_DEFAULTS).map((key) => [
            key,
            state[key],
          ]),
        ),
      },
    })),
  loadDtFiltersForCountry: (countryId) =>
    set((state) => ({
      // Defaults primero: si el snapshot es de una versión vieja sin alguna
      // clave, esa clave vuelve a su default en vez de quedar indefinida.
      ...DT_PERSISTED_FILTER_DEFAULTS,
      ...(state.dtFiltersByCountry[countryId] ?? {}),
    })),
  getDtFiltersAreDefault: () => {
    const state = get();
    return (
      state.dealsToolSelectedBrand === null &&
      state.dtSearch.length === 0 &&
      state.dtSelectedCategoryPath?.length === 0 &&
      state.dtSelectedDomains.length === 0 &&
      state.dtSelectedTimeRange === "today" &&
      state.dtRangeDate === null &&
      state.dtSelectedTypeofcampaign?.length === 0 &&
      state.dtSelectedStatus?.length === 0 &&
      Object.keys(state.dtEditedRows).length === 0 &&
      Object.keys(state.dtValidationErrors).length === 0 &&
      state.dtRowsSelectionModel.length === 0 &&
      state.dtFeedbackByRow.length === 0 &&
      state.dtSelectedKam.length === 0 &&
      state.dtSelectedPromotionName?.length === 0
    );
  },
  setDtSelectedKam: (kam) => set(() => ({ dtSelectedKam: kam })),
  clearDtSelectedKam: () => set(() => ({ dtSelectedKam: [] })),
  setdtAllRows: (rows) => set(() => ({ dtAllRows: rows })),
  setDtRawRows: (rows) => set(() => ({ dtRawRows: rows })),
  setDtFilteredRows: (rows) => set(() => ({ dtFilteredRows: rows })),
  setDtPaginationModel: (dtPaginationModel) =>
    set(() => ({ dtPaginationModel, dtExpandedRows: {} })),
  clearDtPaginationModel: () =>
    set(() => ({ dtPaginationModel: { page: 0, pageSize: 10 } })),
  updateDtPaginationModel: (dtPaginationModel) =>
    set((state) => ({
      dtPaginationModel: { ...state.dtPaginationModel, ...dtPaginationModel },
      dtExpandedRows: {},
    })),
  toggleDtEditedRow: (rowId, row) =>
    set((state) => {
      // Una promo con action_button = 'N/A' no se puede tocar: no permitimos
      // seleccionarla. (Sí permitimos des-seleccionar si por algún motivo ya estaba.)
      if (!isRowSelectable(row) && !state.dtEditedRows[rowId]) {
        return state;
      }
      const newEditedRows = { ...state.dtEditedRows };
      let newValidationErrors = { ...state.dtValidationErrors };
      let newSelectionModel = [...state.dtRowsSelectionModel];

      if (newEditedRows[rowId]) {
        // Al deseleccionar, limpiar los errores de validación de esta fila
        delete newEditedRows[rowId];
        // Filtrar errores que pertenezcan a este rowId
        newValidationErrors = Object.fromEntries(
          Object.entries(newValidationErrors).filter(
            ([key]) => !key.startsWith(`${rowId}-`),
          ),
        );
        newSelectionModel = newSelectionModel.filter((id) => id !== rowId);
      } else {
        // Guardar la fila con valueOriginal y valueEdited en cada campo editable
        newEditedRows[rowId] = {
          ...row,
          priceregular: {
            ...row.priceregular,
            valueOriginal: row.priceregular?.value,
            valueEdited: row.priceregular?.value,
          },
          discountseller: {
            ...row.discountseller,
            valueOriginal: row.discountseller?.value,
            valueEdited: row.discountseller?.value,
          },
          priceoffer: {
            ...row.priceoffer,
            valueOriginal: row.priceoffer?.value,
            valueEdited: row.priceoffer?.value,
          },
          publicprice: {
            ...row.publicprice,
            valueOriginal: row.publicprice?.value,
            valueEdited: row.publicprice?.value,
          },
          rebate: {
            ...row.rebate,
            valueOriginal: row.rebate?.value,
            valueEdited: row.rebate?.value,
          },
          margen: {
            ...row.margen,
            valueOriginal: row.margen?.value,
            valueEdited: row.margen?.value,
          },
          roi: {
            ...row.roi,
            valueOriginal: row.roi?.value,
            valueEdited: row.roi?.value,
          },
        };
        if (!newSelectionModel.includes(rowId)) {
          newSelectionModel.push(rowId);
        }
      }
      return {
        dtEditedRows: newEditedRows,
        dtValidationErrors: newValidationErrors,
        dtRowsSelectionModel: newSelectionModel,
      };
    }),
  addDtEditedRow: (rowId, row) =>
    set((state) => ({
      dtEditedRows: { ...state.dtEditedRows, [rowId]: row },
      dtRowsSelectionModel: [...state.dtRowsSelectionModel, rowId],
    })),
  removeDtEditedRow: (rowId) =>
    set((state) => {
      const newEditedRows = { ...state.dtEditedRows };
      delete newEditedRows[rowId];
      return {
        dtEditedRows: newEditedRows,
        dtRowsSelectionModel: state.dtRowsSelectionModel.filter(
          (id) => id !== rowId,
        ),
      };
    }),
  setAllDtEditedRows: (incomingEditedRows) =>
    set((state) => {
      // Excluimos de la selección masiva las promos que no se pueden tocar (action_button = 'N/A').
      const newEditedRows = Object.fromEntries(
        Object.entries(incomingEditedRows).filter(([, row]) =>
          isRowSelectable(row),
        ),
      );
      if (Object.keys(newEditedRows).length === 0) {
        return {
          dtEditedRows: newEditedRows,
          dtValidationErrors: {},
          dtRowsSelectionModel: [],
        };
      }
      const newRowIds = Object.keys(newEditedRows);
      const newValidationErrors = Object.fromEntries(
        Object.entries(state.dtValidationErrors).filter(([key]) =>
          newRowIds.some((rowId) => key.startsWith(`${rowId}-`)),
        ),
      );
      return {
        dtEditedRows: newEditedRows,
        dtValidationErrors: newValidationErrors,
        dtRowsSelectionModel: newRowIds,
      };
    }),

  // Funciones para manejar errores de validación
  setDtValidationError: (rowId, field, errorMessage) =>
    set((state) => ({
      dtValidationErrors: {
        ...state.dtValidationErrors,
        [`${rowId}-${field}`]: errorMessage,
      },
    })),
  clearDtValidationError: (rowId, field) =>
    set((state) => {
      const newErrors = { ...state.dtValidationErrors };
      delete newErrors[`${rowId}-${field}`];
      return { dtValidationErrors: newErrors };
    }),
  hasDtValidationErrors: () => {
    const state = get();
    return Object.keys(state.dtValidationErrors).length > 0;
  },

  editDtField: (row, field, newValue) =>
    set((state) => {
      const existingEditedRow = state.dtEditedRows[row.id];

      // Obtener el valor original y preservar editable
      const existingField = existingEditedRow?.[field];
      const valueOriginal = existingField?.valueOriginal ?? row[field]?.value;
      // Preservar editable del campo original o del existente
      const editable = row[field]?.editable ?? existingField?.editable;
      const isEditable = row[field]?.isEditable ?? existingField?.isEditable;
      const planId = row[field]?.planId ?? existingField?.planId;

      return {
        dtEditedRows: {
          ...state.dtEditedRows,
          [row.id]: {
            ...row,
            ...existingEditedRow,
            [field]: {
              ...row[field],
              ...existingField,
              valueOriginal,
              valueEdited: newValue,
              value: newValue,
              editable,
              isEditable,
              planId,
            },
          },
        },
      };
    }),

  // Aplica el list_cost fresco que devuelve MELI (vía /GetMeliFreeShipping) al recálculo de
  // margen/roi del DEAL. AR/MX/CO. El override se persiste en `_meliListCostOverride` para
  // que las ediciones siguientes del precio lo reutilicen (sin flicker contra el valor cacheado)
  // hasta que un nuevo fetch lo reemplace. Espeja a pricing.applyMeliFreeShipping.
  applyDtMeliFreeShipping: (row, meliListCost) =>
    set((state) => {
      if (!meliListCost || meliListCost <= 0) return state;

      const existingEditedRow = state.dtEditedRows[row.id];
      const base = existingEditedRow ?? row;

      // Precio vigente: priceoffer editado o el original.
      const rawOffer =
        base.priceoffer?.valueEdited ??
        base.priceoffer?.value ??
        row.priceoffer?.value;
      const price = Number(rawOffer);
      if (!Number.isFinite(price) || price <= 0) return state;

      const { margen, roi } = calculateMarginAndRoi(row, price, meliListCost);

      // Construye el campo con la misma forma que editDtField para que la celda renderice.
      const buildField = (field, newValue) => {
        const existingField = existingEditedRow?.[field];
        return {
          ...row[field],
          ...existingField,
          valueOriginal: existingField?.valueOriginal ?? row[field]?.value,
          valueEdited: newValue,
          value: newValue,
          editable: row[field]?.editable ?? existingField?.editable,
          isEditable: row[field]?.isEditable ?? existingField?.isEditable,
        };
      };

      return {
        dtEditedRows: {
          ...state.dtEditedRows,
          [row.id]: {
            ...row,
            ...existingEditedRow,
            margen: buildField("margen", margen),
            roi: buildField("roi", roi),
            _meliListCostOverride: meliListCost,
          },
        },
      };
    }),

  clearDtFieldEdit: (rowId, field, originalRow) =>
    set((state) => {
      const existingEditedRow = state.dtEditedRows[rowId];
      if (!existingEditedRow) return state;

      const existingField = existingEditedRow[field];
      const valueOriginal =
        existingField?.valueOriginal ?? originalRow[field]?.value;
      // Preservar editable del campo original o del existente
      const editable = originalRow[field]?.editable ?? existingField?.editable;
      const isEditable =
        originalRow[field]?.isEditable ?? existingField?.isEditable;

      // Restaurar el campo al valor original
      const updatedRow = {
        ...existingEditedRow,
        [field]: {
          ...originalRow[field],
          valueOriginal,
          valueEdited: valueOriginal,
          value: valueOriginal,
          editable,
          isEditable,
        },
      };

      // Verificar si hay cambios pendientes en algún campo
      const editableFields = [
        "priceregular",
        "discountseller",
        "priceoffer",
        "publicprice",
        "rebate",
        "margen",
        "roi",
      ];
      const hasChanges = editableFields.some((key) => {
        const fieldData = updatedRow[key];
        if (
          fieldData?.valueOriginal !== undefined &&
          fieldData?.valueEdited !== undefined
        ) {
          return fieldData.valueOriginal !== fieldData.valueEdited;
        }
        return false;
      });

      if (!hasChanges) {
        const newEditedRows = { ...state.dtEditedRows };
        delete newEditedRows[rowId];
        return {
          dtEditedRows: newEditedRows,
          dtRowsSelectionModel: state.dtRowsSelectionModel.filter(
            (id) => id !== rowId,
          ),
        };
      }

      return {
        dtEditedRows: {
          ...state.dtEditedRows,
          [rowId]: updatedRow,
        },
      };
    }),
  editDtFieldListingType: (row, newValue, planId) =>
    set((state) => {
      const existingEditedRow = state.dtEditedRows[row.id];

      // Obtener el valor original y preservar editable
      const existingField = existingEditedRow?.["listing_type"];
      const valueOriginal =
        existingField?.valueOriginal ?? row["listing_type"]?.value;
      // Preservar editable del campo original o del existente
      const editable = row["listing_type"]?.editable ?? existingField?.editable;
      const isEditable =
        row["listing_type"]?.isEditable ?? existingField?.isEditable;

      const newSelectionModel = state.dtRowsSelectionModel.includes(row.id)
        ? state.dtRowsSelectionModel
        : [...state.dtRowsSelectionModel, row.id];

      return {
        dtEditedRows: {
          ...state.dtEditedRows,
          [row.id]: {
            ...row,
            ...existingEditedRow,
            ["listing_type"]: {
              ...row["listing_type"],
              ...existingField,
              valueOriginal,
              valueEdited: newValue,
              value: newValue,
              planId,
              editable,
              isEditable,
            },
          },
        },
        dtRowsSelectionModel: newSelectionModel,
      };
    }),
  setDealsToolSelectedBrand: (brand) =>
    set(() => ({
      dealsToolSelectedBrand: brand,
      dtRowsSelectionModel: [],
      dtEditedRows: {},
    })),
  clearDealsToolSelectedBrand: () =>
    set((state) => ({
      dealsToolSelectedBrand: null,
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  setDtSearch: (newSearch) =>
    set((state) => {
      const current = state.dtSearch || [];

      const incoming = Array.isArray(newSearch) ? newSearch : [newSearch];

      const normalized = incoming
        .map((x) => {
          if (!x) return null;

          if (typeof x === "object" && x.value && x.type) {
            const skuValue = x.sku || "";
            return {
              value: x.value.trim(),
              type: x.type,
              sku: Array.isArray(skuValue) ? skuValue : skuValue,
            };
          }

          if (typeof x === "string" && x.trim()) {
            return { value: x.trim(), type: "All", sku: "" };
          }
          return null;
        })
        .filter((x) => x !== null);

      // Filtrar duplicados por valor
      const cleaned = normalized.filter(
        (item) =>
          !current.some((c) => {
            const currentValue = typeof c === "string" ? c : c.value;
            return currentValue === item.value;
          }),
      );

      return {
        dtSearch: [...current, ...cleaned],
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),
  deleteDtSearch: (item) =>
    set((state) => ({
      dtSearch: state.dtSearch.filter((s) => {
        if (typeof s === "string" && typeof item === "string") {
          return s !== item;
        }
        if (typeof s === "object" && typeof item === "object") {
          return s.value !== item.value;
        }
        const sValue = typeof s === "string" ? s : s.value;
        const itemValue = typeof item === "string" ? item : item.value;
        return sValue !== itemValue;
      }),
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtSearch: () =>
    set((state) => ({
      dtSearch: [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),

  toggleDtSelectedCategoryPath: (categoryPath) =>
    set((state) => {
      const isSelected = state.dtSelectedCategoryPath?.includes(categoryPath);
      const newCategoryPath = isSelected
        ? state.dtSelectedCategoryPath?.filter((c) => c !== categoryPath)
        : [...(state.dtSelectedCategoryPath || []), categoryPath];
      return {
        dtSelectedCategoryPath: newCategoryPath,
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),

  toggleDtSelectedDomain: (domain) =>
    set((state) => {
      const isSelected = state.dtSelectedDomains?.includes(domain);
      const newDomains = isSelected
        ? state.dtSelectedDomains?.filter((d) => d !== domain)
        : [...(state.dtSelectedDomains || []), domain];
      return {
        dtSelectedDomains: newDomains,
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),
  setDtDomains: (domains) => set(() => ({ dtDomains: domains })),
  setDtColumns: (columns) => set(() => ({ dtColumns: columns })),
  resetDtColumns: () => set(() => ({ dtColumns: INITIAL_COLUMNS_STATE })),
  toggleDtColumns: (column, checked) =>
    set((state) => ({
      dtColumns: { ...state.dtColumns, [column]: checked },
    })),
  updateDtRowsSelectionModelAndEditedRows: (model) =>
    set((state) => ({ dtRowsSelectionModel: model })),
  setDtRows: (rows) => set({ dtRows: rows }),
  setDtRangeDate: (date) =>
    set((state) => ({
      dtRangeDate: date,
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  setDtSelectedTimeRange: (timeRange) =>
    set((state) => ({
      dtSelectedTimeRange: timeRange,
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtSelectedTimeRange: () =>
    set((state) => ({
      dtSelectedTimeRange: "today",
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtRangeDate: () =>
    set((state) => ({
      dtRangeDate: null,
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  setDtSelectedTypeofcampaign: (typeofcampaign) =>
    set((state) => ({
      dtSelectedTypeofcampaign: typeofcampaign,
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtSelectedTypeofcampaign: () =>
    set((state) => ({
      dtSelectedTypeofcampaign: [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  toggleDtSelectedTypeofcampaign: (typeofcampaign) =>
    //Seleccion multiple de tipos de campañas
    set((state) => {
      const isSelected =
        state.dtSelectedTypeofcampaign?.includes(typeofcampaign);
      const newTypeofcampaign = isSelected
        ? state.dtSelectedTypeofcampaign?.filter((t) => t !== typeofcampaign)
        : [...(state.dtSelectedTypeofcampaign || []), typeofcampaign];
      return {
        dtSelectedTypeofcampaign: newTypeofcampaign,
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),

  setDtSelectedStatus: (status) =>
    set((state) => ({
      dtSelectedStatus: [status],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtSelectedStatus: () =>
    set((state) => ({
      dtSelectedStatus: [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  toggleSelectedStatus: (status) =>
    //Seleccion multiple de estados
    set((state) => {
      const isSelected = state.dtSelectedStatus?.includes(status);
      const newStatus = isSelected
        ? state.dtSelectedStatus?.filter((s) => s !== status)
        : [...(state.dtSelectedStatus || []), status];
      return {
        dtSelectedStatus: newStatus,
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),
  // Filtro de Promoción (multi-select autocomplete): matchea por promotion_name exacto (OR).
  setDtSelectedPromotionName: (promotionNames) =>
    set(() => ({
      dtSelectedPromotionName: Array.isArray(promotionNames)
        ? promotionNames
        : [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtSelectedPromotionName: () =>
    set(() => ({
      dtSelectedPromotionName: [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  toggleDtSelectedPromotionName: (promotionName) =>
    set((state) => {
      const isSelected =
        state.dtSelectedPromotionName?.includes(promotionName);
      const newPromotionNames = isSelected
        ? state.dtSelectedPromotionName?.filter((p) => p !== promotionName)
        : [...(state.dtSelectedPromotionName || []), promotionName];
      return {
        dtSelectedPromotionName: newPromotionNames,
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),
  setDtFeedbackByRow: (feedback) =>
    set(() => ({
      dtFeedbackByRow: feedback,
    })),
  clearDtFeedbackForRow: (rowKey) =>
    set((state) => {
      const copy = { ...state.dtFeedbackByRow };
      delete copy[rowKey];
      return { dtFeedbackByRow: copy };
    }),
  importDtChanges: (changes, allRows) => {
    const state = get();
    // Snapshot previo al import para poder Cancelar la revisión y restaurar la vista.
    const prevEditedRows = state.dtEditedRows;
    const prevSelection = state.dtRowsSelectionModel;
    const newEditedRows = { ...state.dtEditedRows };
    const newSelectionModel = [...state.dtRowsSelectionModel];
    const appliedIds = [];
    let appliedCount = 0;
    // Promos con cambio de precio: alimentan la consulta real-time a MELI tras el import
    // (refina margen/ROI con el list_cost fresco, igual que la edición manual). Ver ImportButton.
    const meliTargets = [];
    // Filas no seleccionables (action_button 'N/A': ya activas o bloqueadas por MeLi) que llegaron
    // por el import. La edición manual ya las bloquea (isRowSelectable en toggleDtEditedRow), pero el
    // import no pasaba por ese guard → se omiten acá y se avisan en ImportButton.
    const skipped = [];

    const NUMERIC_FIELDS = [
      "priceregular",
      "discountseller",
      "priceoffer",
      "publicprice",
      "rebate",
      "margen",
      "roi",
    ];

    changes.forEach((change) => {
      const {
        id,
        sku,
        promotionname,
        listing_type: newListingType,
        plan_cuotas: newPlanCuotas,
        isListingRow,
        action: importedAction,
        ref_id: refId,
        offer_id: offerId,
        ...fieldChanges
      } = change;

      // Prioridad de match:
      // 1) Identificadores DISCRETOS (ref_id / offer_id): vía certera para MODIFICAR la promo
      //    correcta, incluso cuando varias comparten deal_id. Estables aunque cambie el
      //    nombre/título (a diferencia del ID compuesto). Los lee ImportDealsTool.
      // 2) ID compuesto: `itemid|sku|deal_id_subId|tipo|promo` (archivos viejos sin columnas discretas).
      // 3) sku + promotionname (último recurso).
      const matchesDiscreteId = (r) =>
        !r.isParentRow &&
        ((refId != null && r.ref_id != null && String(r.ref_id) === refId) ||
          (offerId != null && r.offer_id != null && String(r.offer_id) === offerId));

      const row =
        (!isListingRow && (refId != null || offerId != null)
          ? allRows.find(matchesDiscreteId)
          : null) ??
        (id != null
          ? allRows.find((r) => {
              if (String(r.id) === String(id)) return true;
              const subId = r.ref_id ?? r.offer_id ?? null;
              const baseId = r.deal_id
                ? subId ? `${r.deal_id}_${subId}` : String(r.deal_id)
                : (subId != null ? String(subId) : null);
              if (baseId == null) return false;
              const composed = `${r.itemid ?? ""}|${r.sku ?? ""}|${baseId}|${r.typeofcampaign ?? ""}|${r.promotionname ?? ""}`;
              return composed === String(id);
            })
          : null) ??
        (sku
          ? allRows.find(
              (r) =>
                r.sku === sku &&
                !!r.isParentRow === !!isListingRow &&
                (!promotionname || r.promotionname === promotionname),
            )
          : null);

      if (!row) return;

      // Promo no modificable (N/A): no se aplica y se cuenta como omitida. Mismo criterio que
      // la edición manual (isRowSelectable). Los listings devuelven true, no se ven afectados.
      if (!isRowSelectable(row)) {
        skipped.push(row.promotionname || row.sku || String(row.id ?? ""));
        return;
      }

      const existing = newEditedRows[row.id] ?? {};
      const updatedFields = {};

      // Campos numéricos
      NUMERIC_FIELDS.forEach((key) => {
        const newValue = fieldChanges[key];
        if (newValue === null || newValue === undefined) return;

        const originalValue = existing?.[key]?.valueOriginal ?? row[key]?.value;

        if (Number(newValue) === Number(originalValue)) return;

        updatedFields[key] = {
          ...row[key],
          ...(existing?.[key] ?? {}),
          valueOriginal: originalValue,
          valueEdited: newValue,
          value: newValue,
        };
      });

      // Recalcular margen/ROI/descuento con el PVP Disbyte (priceoffer) importado, igual que
      // la edición manual (EditNumberInput + calculateMarginAndRoi). El margen SIEMPRE se
      // calcula sobre el priceoffer. Sin esto el import mostraba el precio nuevo pero
      // conservaba el margen/ROI viejos hasta editar la celda. En bulk no hay override
      // real-time de MELI: usa el shipping_cost cacheado (luego ImportButton lo afina).
      //
      // SOLO en promociones: el margen del Listing es el del precio de oferta y NO depende del
      // PVP que se importa (solo afecta el descuento). La UI tampoco lo recalcula al editar el
      // PVP del Listing (ver createParentRow y el gate field==="priceoffer" en EditNumberInput).
      const cv = row.calc_values;
      const newOffer = updatedFields.priceoffer?.value;
      if (
        !isListingRow &&
        newOffer != null &&
        cv?.active_price > 0 &&
        cv?.iva_rate > 0
      ) {
        const { margen, roi } = calculateMarginAndRoi(row, Number(newOffer));

        // Misma forma de campo que applyDtMeliFreeShipping/editDtField para que renderice.
        const buildCalcField = (field, value) => {
          const existingField = existing?.[field];
          return {
            ...row[field],
            ...existingField,
            valueOriginal: existingField?.valueOriginal ?? row[field]?.value,
            valueEdited: value,
            value,
            editable: row[field]?.editable ?? existingField?.editable,
            isEditable: row[field]?.isEditable ?? existingField?.isEditable,
          };
        };

        updatedFields.margen = buildCalcField("margen", margen);
        updatedFields.roi = buildCalcField("roi", roi);

        // Descuento seller = (PVP - PVP Disbyte) / PVP * 100.
        const priceRegular = Number(row.priceregular?.value);
        if (priceRegular > 0) {
          const descuento =
            Math.round(
              ((priceRegular - Number(newOffer)) / priceRegular) * 10000,
            ) / 100;
          updatedFields.discountseller = buildCalcField(
            "discountseller",
            descuento,
          );
        }
      }

      // Tipo de Publicación
      const incomingLTValue = newListingType?.value ?? null;
      const incomingPlanId = newListingType?.planId ?? null;

      const currentRawLT =
        existing?.listing_type?.valueEdited ??
        row.listing_type?.value ??
        row.listing_type_name ??
        "";
      const currentNormalizedLT =
        currentRawLT?.toLowerCase().includes("special") ||
        currentRawLT?.toLowerCase().includes("clásica") ||
        currentRawLT === "gold_special"
          ? "Clásica"
          : currentRawLT
            ? "Premium"
            : "";

      if (incomingLTValue && incomingLTValue !== currentNormalizedLT) {
        const valueOriginal =
          existing?.listing_type?.valueOriginal ?? currentNormalizedLT;

        updatedFields.listing_type = {
          ...(row.listing_type ?? {}),
          ...(existing?.listing_type ?? {}),
          valueOriginal,
          valueEdited: incomingLTValue,
          value: incomingLTValue,
          planId: incomingPlanId ?? existing?.listing_type?.planId ?? null,
        };
      }

      // Plan de cuotas
      const currentTag = row.tags?.find(
        (t) =>
          !t.includes("mshops") &&
          (t.includes("campaign") ||
            t.includes("co-funded") ||
            t.includes("special")),
      );

      const CAMPAIGN_ID_TO_LABEL = {
        "no-campaign": "No agregar cuotas",
        gold_special: "3 a 12 cuotas con interés bajo",
        "3x_campaign": "3 cuotas al mismo precio que publiques",
        "6x_campaign": "6 cuotas al mismo precio que publiques",
        "9x_campaign": "9 cuotas al mismo precio que publiques",
        "12x_campaign": "12 cuotas al mismo precio que publiques",
      };
      const LABEL_TO_CAMPAIGN_ID = Object.fromEntries(
        Object.entries(CAMPAIGN_ID_TO_LABEL).map(([k, v]) => [v, k]),
      );

      let currentCuotasLabel = CAMPAIGN_ID_TO_LABEL[currentTag];
      if (!currentCuotasLabel) {
        currentCuotasLabel =
          currentNormalizedLT === "Clásica"
            ? "No agregar cuotas"
            : "3 cuotas al mismo precio que publiques";
      }

      const currentStoredCuotas =
        existing?.plan_cuotas?.valueEdited ?? currentCuotasLabel;

      if (newPlanCuotas && newPlanCuotas !== currentStoredCuotas) {
        const newCampaignId = LABEL_TO_CAMPAIGN_ID[newPlanCuotas] ?? null;

        updatedFields.plan_cuotas = {
          valueOriginal:
            existing?.plan_cuotas?.valueOriginal ?? currentCuotasLabel,
          valueEdited: newPlanCuotas,
          value: newPlanCuotas,
        };
        const baseLT =
          updatedFields.listing_type ??
          existing?.listing_type ??
          row.listing_type ??
          {};
        updatedFields.listing_type = {
          ...baseLT,
          planId: {
            ...(baseLT?.planId ?? {}),
            campaign_id: newCampaignId,
          },
        };
      }

      // Acción de participación (sin cambio de precio)
      if (
        !isListingRow &&
        importedAction &&
        (importedAction === "Participar" ||
          importedAction === "Dejar de Participar")
      ) {
        updatedFields._importedAction = importedAction;
      }

      if (Object.keys(updatedFields).length === 0) return;

      const { _importedAction, ...cleanFields } = updatedFields;
      newEditedRows[row.id] = {
        ...row,
        ...existing,
        ...cleanFields,
        ...(_importedAction ? { action: _importedAction } : {}),
      };

      if (!newSelectionModel.includes(row.id)) newSelectionModel.push(row.id);
      appliedIds.push(row.id);
      appliedCount++;

      // Solo promociones con PVP Disbyte nuevo: el costo de envío fresco de MELI aplica a
      // priceoffer (el Listing no consulta MELI ni en edición manual).
      if (!isListingRow && updatedFields.priceoffer?.value != null) {
        meliTargets.push({ row, price: Number(updatedFields.priceoffer.value) });
      }
    });

    set({
      dtEditedRows: newEditedRows,
      dtRowsSelectionModel: newSelectionModel,
      // Entra en modo revisión solo si efectivamente se aplicó algo. Resetea la página a 0
      // para que el lote (suele ser pocas filas) sea visible aunque estuvieras en otra página.
      ...(appliedIds.length > 0
        ? {
            dtImportReview: {
              active: true,
              ids: appliedIds,
              prevEditedRows,
              prevSelection,
            },
            dtPaginationModel: { ...state.dtPaginationModel, page: 0 },
          }
        : {}),
    });

    return { appliedCount, meliTargets, skipped };
  },
  acceptDtImportReview: () =>
    set({
      dtImportReview: {
        active: false,
        ids: [],
        prevEditedRows: null,
        prevSelection: null,
      },
    }),
  cancelDtImportReview: () =>
    set((state) => {
      const { ids, prevEditedRows, prevSelection } = state.dtImportReview;
      // Limpia los errores de validación generados por el lote (clave = `${rowId}-${field}`).
      const restoredErrors = Object.fromEntries(
        Object.entries(state.dtValidationErrors).filter(
          ([key]) => !ids.some((rid) => key.startsWith(`${rid}-`)),
        ),
      );
      return {
        dtEditedRows: prevEditedRows ?? {},
        dtRowsSelectionModel: prevSelection ?? [],
        dtValidationErrors: restoredErrors,
        dtImportReview: {
          active: false,
          ids: [],
          prevEditedRows: null,
          prevSelection: null,
        },
      };
    }),
  clearDtEditedRows: () =>
    set({
      dtEditedRows: {},
      // Al limpiar edits (p.ej. tras publicar) también se sale de la revisión de import
      // para no dejar el filtro activo con ids que ya no existen.
      dtImportReview: {
        active: false,
        ids: [],
        prevEditedRows: null,
        prevSelection: null,
      },
    }),
  setDtRowSelectionModel: (model) => set({ dtRowsSelectionModel: model }),
  setDtExpandedRows: (rowId, expanded) =>
    set((state) => ({
      dtExpandedRows: {
        ...state.dtExpandedRows,
        [rowId]: expanded,
      },
    })),
  setBatchDtExpandedRows: (expandedRowsMap) =>
    set(() => ({
      dtExpandedRows: expandedRowsMap,
    })),
  setDtSelectedMeliStatus: (status) =>
    set((state) => ({
      dtSelectedMeliStatus: [status],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  clearDtSelectedMeliStatus: () =>
    set((state) => ({
      dtSelectedMeliStatus: [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
  toggleDtSelectedMeliStatus: (status) =>
    //Seleccion multiple de estados
    set((state) => {
      const isSelected = state.dtSelectedMeliStatus?.includes(status);
      const newStatus = isSelected
        ? state.dtSelectedMeliStatus?.filter((s) => s !== status)
        : [...(state.dtSelectedMeliStatus || []), status];
      return {
        dtSelectedMeliStatus: newStatus,
        dtRowsSelectionModel: [],
        dtEditedRows: {},
        dtExpandedRows: {},
      };
    }),
  toggleDtShowMeliFlags: () =>
    set((state) => ({ dtShowMeliFlags: !state.dtShowMeliFlags })),
  clearDtAllFilters: () =>
    set((state) => ({
      dtSelectedCategoryPath: [],
      dtSelectedDomains: [],
      dtSearch: [],
      dealsToolSelectedBrand: null,
      dtSelectedTimeRange: "today",
      dtRangeDate: null,
      dtSelectedTypeofcampaign: [],
      dtSelectedStatus: [],
      dtSelectedMeliStatus: [],
      dtSelectedPromotionName: [],
      dtRowsSelectionModel: [],
      dtEditedRows: {},
      dtExpandedRows: {},
    })),
});
